import {Item} from '@/store/models';

export default class DokumentWrapper {
    doc: any;

    constructor(doc: any) {
        this.doc = doc;
    }

    getItem(id: number) {
        return this.doc.Items.filter((x: Item) => x.Id === id)[0];
    }

    getText(type: string) {
        if (this.doc.Texts) {
            return this.doc.Texts[type];
        }
        return undefined;
    }

    get userName() {
        if (this.doc.Meta === null) {
            return '';
        }
        return `${this.doc.Meta.FirstName} ${this.doc.Meta.LastName}`;
    }

    get meta() {
        return this.doc.Meta;
    }

    get overview(): any {
        return this.doc.overview;
    }

    get overviewC1(): any {
        return this.doc.overviewC1;
    }

    get overviewC2(): any {
        return this.doc.overviewC2;
    }

    get profitloss(): any {
        return this.doc.profitloss;
    }

    get profitlossC1(): any {
        return this.doc.profitlossC1;
    }

    get profitlossC2(): any {
        return this.doc.profitlossC2;
    }

    get balance(): any {
        return this.doc.balance;
    }

    get balanceC1(): any {
        return this.doc.balanceC1;
    }

    get balanceC2(): any {
        return this.doc.balanceC2;
    }

    get ranking(): any {
        return this.doc.ranking;
    }

    get rankingC1(): any {
        return this.doc.rankingC1;
    }

    get rankingC2(): any {
        return this.doc.rankingC2;
    }

    get cashflow(): any {
        return this.doc.cashflow;
    }

    get valuation(): any {
        return this.doc.valuation;
    }

    get sustainability(): any {
        return this.doc.sustainability;
    }

    get recommendation(): any {
        return this.getItem(2007).SubType;
    }

    get hasOperatingCashflow() {
        return !(this.cashflow.sheets[0].OperatingResult === 0 &&
            this.cashflow.sheets[0].Adjustment === 0 &&
            this.cashflow.sheets[0].TotFinInvestments === 0 &&
            this.cashflow.sheets[0].Tax === 0);
    }

    get hasWorkingCapitalCashflow() {
        return !(this.cashflow.sheets[0].InventoriesDelta === 0 &&
            this.cashflow.sheets[0].AccountReceivableTradeDelta === 0 &&
            this.cashflow.sheets[0].OtherAccReceiveDelta === 0 &&
            this.cashflow.sheets[0].AccPayTradeDelta === 0 &&
            this.cashflow.sheets[0].OtherCurrentLiabilitiesDelta === 0);
    }

    get hasNegativeWorkingCapitalCashflow() {
        return this.cashflow.sheets[0].InventoriesDelta < 0 ||
            this.cashflow.sheets[0].AccountReceivableTradeDelta < 0 ||
            this.cashflow.sheets[0].OtherAccReceiveDelta < 0 ||
            this.cashflow.sheets[0].AccPayTradeDelta < 0 ||
            this.cashflow.sheets[0].OtherCurrentLiabilitiesDelta < 0;
    }

    get hasPositiveWorkingCapitalCashflow() {
        return this.cashflow.sheets[0].InventoriesDelta > 0 ||
            this.cashflow.sheets[0].AccountReceivableTradeDelta > 0 ||
            this.cashflow.sheets[0].OtherAccReceiveDelta > 0 ||
            this.cashflow.sheets[0].AccPayTradeDelta > 0 ||
            this.cashflow.sheets[0].OtherCurrentLiabilitiesDelta > 0;
    }

    get hasInvestmentsCashflow() {
        return !(this.cashflow.sheets[0].TotFixAssetsDelta === 0 &&
            this.cashflow.sheets[0].DeprAndWriteDowns === 0 &&
            this.cashflow.sheets[0].ShortInvestmentsDelta === 0);
    }

    get subtype() {
        switch (this.doc.SubType) {
            case 'accounting':
                return 'räkenskaps-PM';
            case 'cashflow':
                return 'kassaflödesrapport';
            case 'valuation':
                return 'värderingsrapport';
            case 'company':
                return 'bolagsrapport';
            case 'kyc':
                return 'bolags-PM';
            case 'sustainability':
                return 'hållbarhets-PM';
            }
    }

    get isSie() {
        if (
            this.doc.SieAggVersion === 0
        ) {
            return (
                false
            );
        } else {
            return true;
        }
    }

    transformMonth(monthValue: string) {
        switch (monthValue) {
            case '01':
                return 'januari';
            case '02':
                return 'februari';
            case '03':
                return 'mars';
            case '04':
                return 'april';
            case '05':
                return 'maj';
            case '06':
                return 'juni';
            case '07':
                return 'juli';
            case '08':
                return 'augusti';
            case '09':
                return 'september';
            case '10':
                return 'oktober';
            case '11':
                return 'november';
            case '12':
                return 'december';
        }
    }

    get hasNegativeEquity() {
        return this.doc.balance.sheets[0].M < 0;
    }

    get calculateBurnrate() {
        if (
            this.doc.cashflow.sheets.length > 0 &&
            this.doc.cashflow.sheets[0].OperatingCashflow < 0 &&
            this.doc.balance.sheets[0].J /
            (-this.doc.cashflow.sheets[0].OperatingCashflow /
                this.doc.cashflow.sheets[0].MONTHS) <=
            24
        ) {
            return (
                this.doc.balance.sheets[0].J /
                (-this.doc.cashflow.sheets[0].OperatingCashflow /
                    this.doc.cashflow.sheets[0].MONTHS)
            );
        } else {
            return false;
        }
    }

    get hasHighUntangibleAssets() {
        return this.doc.balance.sheets[0].B > 0 &&
            this.doc.balance.sheets[0].M < this.doc.balance.sheets[0].B;
    }

    get isGazelleCandidate() {
        return this.doc.profitloss.sheets[0].A > 10000 &&
            this.doc.profitloss.sheets[3].A > 500 &&
            this.doc.overview.NR_EMPLOYEES > 10 &&
            this.doc.profitloss.sheets[0].A > this.doc.profitloss.sheets[3].A * 2 &&
            this.doc.profitloss.sheets[0].A > this.doc.profitloss.sheets[1].A &&
            this.doc.profitloss.sheets[1].A > this.doc.profitloss.sheets[2].A &&
            this.doc.profitloss.sheets[2].A > this.doc.profitloss.sheets[3].A &&
            this.doc.profitloss.sheets[0].K +
            this.doc.profitloss.sheets[1].K +
            this.doc.profitloss.sheets[2].K +
            this.doc.profitloss.sheets[3].K >
            0;
    }

    get soonNeedEnviromentReport() {
        return this.doc.profitloss.sheets[0].A < 350000 &&
            this.doc.profitloss.sheets[0].A > 250000 &&
            (this.doc.profitloss.sheets[0].A / this.doc.profitloss.sheets[1].A) *
            this.doc.profitloss.sheets[0].A >
            350000;
    }

    get hasIncreasedWorkingCapital() {
            if (
            // tslint:disable-next-line:max-line-length
            ((this.doc.balance.sheets[0].K - this.doc.balance.sheets[0].J - this.doc.balance.sheets[0].Q) / this.doc.profitloss.sheets[0].A) -
            // tslint:disable-next-line:max-line-length
            ((this.doc.balance.sheets[1].K - this.doc.balance.sheets[1].J - this.doc.balance.sheets[1].Q) / this.doc.profitloss.sheets[1].A)
            > 0.05
        ) {
            return true;
        }
    }

}
